import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faPlus,
  faTrashCan,
  faCheck,
  faCircleCheck,
} from '@fortawesome/free-solid-svg-icons';
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  MeasuringStrategy,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import {
  MsalAuthenticationTemplate,
  InteractionType,
} from 'src/auth/firebaseMsalCompat';
import { LoadingSpinner } from '../components/LoadingSpinner';
import {
  useFingeringUpload,
  isImageFile,
  VALID_IMAGE_MIME_TYPES,
  MAX_IMAGE_BYTES_PER_PAGE,
  imagePageLimitMessage,
} from '../hooks/useFingeringUpload';
import { dragHasOnlyAllowedFiles, isFileDrag } from '../utils/fileDrag';
import { useImagePageLimit } from '../hooks/useImagePageLimit';
import {
  useScanDraft,
  addDraftFiles,
  removeDraftPages,
  reorderDraft,
  clearDraft,
  type ScanDraftPage,
} from '../stores/scanDraftStore';
import { captureFunnelEvent, trackButtonClick } from '../services/telemetry';

const ADD_ACCEPT_ATTR =
  '.jpg,.JPG,.jpeg,.JPEG,.png,.PNG,.webp,.WEBP,image/jpeg,image/png,image/webp';

interface SortablePageCardProps {
  page: ScanDraftPage;
  index: number;
  isSelected: boolean;
  disabled: boolean;
  onToggleSelect: (id: string) => void;
  onPreview: (id: string) => void;
}

/**
 * A single draggable page thumbnail. The whole card is the drag handle
 * (`listeners` spread here); the select checkbox stops pointer propagation so
 * tapping it toggles selection instead of starting a drag. A short click/tap
 * (below the dnd activation threshold) opens the full-size preview.
 */
function SortablePageCard({
  page,
  index,
  isSelected,
  disabled,
  onToggleSelect,
  onPreview,
}: SortablePageCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: page.id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const openPreview = () => {
    // Skip preview when this card was just dragged (click can fire on
    // pointer-up after a drag ends).
    if (disabled || isDragging) return;
    onPreview(page.id);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label={`Preview page ${index + 1}`}
      className={`
        relative aspect-[5/7] touch-none overflow-hidden rounded-lg border
        bg-white shadow-sm transition-colors
        ${disabled ? '' : 'cursor-grab active:cursor-grabbing'}
        ${
          isSelected
            ? 'border-yellow-400 ring-2 ring-yellow-300'
            : 'border-gray-200'
        }
        ${isDragging ? 'z-10 opacity-50' : ''}
      `}
      onClick={openPreview}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openPreview();
        }
      }}
    >
      <img
        src={page.thumbnailUrl}
        alt={`Page ${index + 1}`}
        className="h-full w-full object-contain p-1"
        draggable={false}
      />

      {/* Select checkbox — stops pointer/keyboard propagation so interacting
          with it doesn't start a drag or open the preview. */}
      <button
        type="button"
        onPointerDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          onToggleSelect(page.id);
        }}
        aria-label={
          isSelected ? `Deselect page ${index + 1}` : `Select page ${index + 1}`
        }
        aria-pressed={isSelected}
        className={`
          absolute top-2 left-2 flex h-6 w-6 items-center justify-center
          rounded-md border transition
          ${
            isSelected
              ? 'border-yellow-400 bg-yellow-400 text-gray-800'
              : `
                border-gray-300 bg-white/80 text-transparent
                hover:border-gray-400
              `
          }
        `}
      >
        <FontAwesomeIcon icon={faCheck} className="text-xs" />
      </button>

      {/* Page number badge */}
      <span
        className={`
          absolute right-2 bottom-2 rounded-full bg-gray-800/70 px-2 py-0.5
          text-xs font-medium text-white
        `}
      >
        {index + 1}
      </span>
    </div>
  );
}

/** Full-size page preview: tap the dimmed backdrop (outside the image) to close. */
function PagePreviewLightbox({
  page,
  index,
  onClose,
}: {
  page: ScanDraftPage;
  index: number;
  onClose: () => void;
}) {
  const fullUrl = useMemo(() => URL.createObjectURL(page.file), [page.file]);

  useEffect(() => {
    return () => URL.revokeObjectURL(fullUrl);
  }, [fullUrl]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Page ${index + 1} preview`}
      className={`
        fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4
      `}
    >
      {/* Full-screen backdrop control: click/tap outside the image closes. */}
      <button
        type="button"
        aria-label="Close preview"
        className="absolute inset-0 cursor-default"
        onClick={onClose}
      />
      <img
        src={fullUrl}
        alt={`Page ${index + 1}`}
        className={`
          relative z-10 max-h-full max-w-full object-contain shadow-2xl
        `}
        draggable={false}
      />
    </div>
  );
}

function ScanReview() {
  const navigate = useNavigate();
  const { uploadImagesDirectly, uploadStatus } = useFingeringUpload(true);
  const imagePageLimit = useImagePageLimit();
  const pages = useScanDraft();

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const addInputRef = useRef<HTMLInputElement | null>(null);
  /** A desktop file drag over the add tile: `accept`, or `reject` for a wrong format. */
  const [fileDrag, setFileDrag] = useState<'accept' | 'reject' | null>(null);
  /** Suppress the click that often fires right after a drag ends. */
  const skipPreviewClickRef = useRef(false);

  const previewPage = previewId
    ? pages.find((p) => p.id === previewId)
    : undefined;
  const previewIndex = previewPage
    ? pages.findIndex((p) => p.id === previewPage.id)
    : -1;

  const openPreview = useCallback((id: string) => {
    if (skipPreviewClickRef.current) return;
    setPreviewId(id);
  }, []);

  // Mouse: drag after an 8px move (so clicks/taps still register). Touch:
  // require a short hold before dragging so vertical scrolling keeps working.
  // Keyboard: space/arrows/space for accessible reordering.
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    document.title = 'Arrange pages - PineappleMusicLab';
  }, []);

  // The draft lives only in memory: on a reload or direct navigation there are
  // no staged pages, so there's nothing to arrange — send the user back to the
  // workspace. `isGenerating` guards the post-upload clear so we don't bounce
  // to /workspace while we're navigating to the result page.
  useEffect(() => {
    if (pages.length === 0 && !isGenerating) {
      navigate('/workspace', { replace: true });
    }
  }, [pages.length, isGenerating, navigate]);

  // Prune selection ids that no longer exist (e.g. after a delete).
  useEffect(() => {
    setSelectedIds((prev) => {
      const alive = new Set<string>();
      for (const p of pages) if (prev.has(p.id)) alive.add(p.id);
      return alive.size === prev.size ? prev : alive;
    });
  }, [pages]);

  const toggleSelected = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleDragStart = () => {
    skipPreviewClickRef.current = true;
  };

  const handleDragEnd = (event: DragEndEvent) => {
    // Eat the click that browsers often fire right after a drag ends.
    window.setTimeout(() => {
      skipPreviewClickRef.current = false;
    }, 100);
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = pages.findIndex((p) => p.id === active.id);
    const to = pages.findIndex((p) => p.id === over.id);
    if (from !== -1 && to !== -1) reorderDraft(from, to);
  };

  const handleAddClick = () => {
    if (isGenerating) return;
    if (pages.length >= imagePageLimit) {
      setError(imagePageLimitMessage(imagePageLimit));
      return;
    }
    addInputRef.current?.click();
  };

  const handleAddFiles = useCallback(
    async (fileList: FileList | null) => {
      const files = fileList ? Array.from(fileList) : [];
      if (files.length === 0) return;
      setError(null);

      if (!files.every(isImageFile)) {
        setError('Only image files (JPG, PNG, WEBP) can be added as pages.');
        captureFunnelEvent('upload_fail', {
          source: 'ScanReview',
          is_guest: false,
          kind: 'image',
          initialBytes: files.reduce((sum, f) => sum + f.size, 0),
          reason: 'file_type',
        });
        return;
      }
      if (pages.length + files.length > imagePageLimit) {
        setError(imagePageLimitMessage(imagePageLimit));
        return;
      }
      const oversized = files.find((f) => f.size > MAX_IMAGE_BYTES_PER_PAGE);
      if (oversized) {
        setError(
          `Image "${oversized.name}" exceeds the 10MB per-page limit. Please choose a smaller image.`
        );
        captureFunnelEvent('upload_fail', {
          source: 'ScanReview',
          is_guest: false,
          kind: 'image',
          initialBytes: files.reduce((sum, f) => sum + f.size, 0),
          reason: 'file_oversize',
        });
        return;
      }
      await addDraftFiles(files);
    },
    [pages.length, imagePageLimit]
  );

  // Card reordering is a pointer drag, not an HTML5 one, so it never
  // carries `Files` and these handlers ignore it.
  const handleAddDragOver = (e: DragEvent<HTMLButtonElement>) => {
    if (!isFileDrag(e.dataTransfer.types)) return;
    // Without this the browser opens a dropped image in the tab.
    e.preventDefault();
    if (!canAddMore || isGenerating) {
      e.dataTransfer.dropEffect = 'none';
      setFileDrag(null);
      return;
    }
    const images = dragHasOnlyAllowedFiles(
      e.dataTransfer.items,
      VALID_IMAGE_MIME_TYPES
    );
    e.dataTransfer.dropEffect = images ? 'copy' : 'none';
    setFileDrag(images ? 'accept' : 'reject');
  };

  const handleAddDragLeave = (e: DragEvent<HTMLButtonElement>) => {
    // Moving onto the icon or label fires a leave on the tile itself.
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
    setFileDrag(null);
  };

  const handleAddDrop = (e: DragEvent<HTMLButtonElement>) => {
    if (!isFileDrag(e.dataTransfer.types)) return;
    e.preventDefault();
    setFileDrag(null);
    if (isGenerating) return;
    if (!canAddMore) {
      setError(imagePageLimitMessage(imagePageLimit));
      return;
    }
    trackButtonClick('ScanReview', 'Drop Pages', {
      count: e.dataTransfer.files.length,
    });
    void handleAddFiles(e.dataTransfer.files);
  };

  // A file dropped anywhere but the add tile (or on it while it is
  // disabled) would open in the tab and lose the draft.
  useEffect(() => {
    const blockStrayDrop = (e: globalThis.DragEvent) => {
      if (!e.dataTransfer || e.defaultPrevented) return;
      if (!isFileDrag(e.dataTransfer.types)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'none';
    };
    window.addEventListener('dragover', blockStrayDrop);
    window.addEventListener('drop', blockStrayDrop);
    return () => {
      window.removeEventListener('dragover', blockStrayDrop);
      window.removeEventListener('drop', blockStrayDrop);
    };
  }, []);

  const handleDeleteSelected = () => {
    if (selectedIds.size === 0) return;
    trackButtonClick('ScanReview', 'Delete Pages', { count: selectedIds.size });
    removeDraftPages(selectedIds);
    setSelectedIds(new Set());
  };

  const handleBack = () => {
    trackButtonClick('ScanReview', 'Back to Workspace');
    clearDraft();
    navigate('/workspace');
  };

  const handleGenerate = async () => {
    if (pages.length === 0 || isGenerating) return;
    setError(null);
    setIsGenerating(true);
    const initialBytes = pages.reduce((sum, p) => sum + p.file.size, 0);
    const pageCount = pages.length;
    const uploadProps = {
      source: 'ScanReview',
      is_guest: false,
      kind: 'image' as const,
      initialBytes,
      pageCount,
      upload_method: 'scan',
    };
    try {
      trackButtonClick('ScanReview', 'Generate fingering', { pageCount });
      captureFunnelEvent('upload_started', { ...uploadProps });
      const result = await uploadImagesDirectly(pages.map((p) => p.file));
      if (result?.success && result.projectId) {
        captureFunnelEvent('upload_succeeded', {
          ...uploadProps,
          isPdf: true,
          needsHandSelection: Boolean(result.needsHandSelection),
          layout: result.layout ?? null,
        });
        const projectId = result.projectId;
        clearDraft();
        navigate(`/pdf-project/${projectId}`);
        return;
      }
      captureFunnelEvent('upload_fail', {
        ...uploadProps,
        reason: 'upload_unsuccessful',
      });
      setError(
        result?.error ||
          uploadStatus ||
          'Something went wrong. Please try again.'
      );
      setIsGenerating(false);
    } catch (err) {
      console.error('Image generation failed:', err);
      captureFunnelEvent('upload_fail', {
        ...uploadProps,
        reason: (err as { message?: string })?.message || 'exception',
      });
      setError('Something went wrong starting generation. Please try again.');
      setIsGenerating(false);
    }
  };

  const canAddMore = pages.length < imagePageLimit;

  return (
    <div
      className={`
        flex min-h-[calc(100vh-4rem)] flex-col bg-[#fbfcfc] font-[Outfit]
      `}
    >
      {/* Sticky action bar */}
      <div
        className={`
          sticky top-0 z-20 flex items-center justify-between gap-3 border-b
          border-gray-200 bg-[#fbfcfc]/90 px-4 py-3 backdrop-blur
          sm:px-6
        `}
      >
        <button
          type="button"
          onClick={handleBack}
          disabled={isGenerating}
          className={`
            inline-flex items-center gap-2 rounded-full border border-gray-700
            bg-white px-5 py-2 text-sm text-gray-700 transition-transform
            duration-100
            hover:bg-gray-50
            active:scale-95
            disabled:cursor-not-allowed disabled:opacity-50
          `}
        >
          <FontAwesomeIcon icon={faChevronLeft} className="text-xs" />
          My Project
        </button>

        <button
          type="button"
          onClick={handleGenerate}
          disabled={pages.length === 0 || isGenerating}
          className={`
            inline-flex items-center gap-2 rounded-full bg-gray-800 px-6 py-2
            text-sm font-medium text-white transition-transform duration-100
            hover:bg-gray-700
            active:scale-95
            disabled:cursor-not-allowed disabled:opacity-50
          `}
        >
          <FontAwesomeIcon icon={faCircleCheck} className="text-xs" />
          {isGenerating ? 'Generating…' : 'Generate fingering'}
        </button>
      </div>

      <div className="mx-auto w-full max-w-5xl flex-grow px-4 py-6 sm:px-6">
        {/* Title + count + selection actions */}
        <div className="mb-6 flex items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-light text-gray-800">Arrange pages</h1>
            <p className="mt-1 text-sm text-gray-500">
              {pages.length} {pages.length === 1 ? 'page' : 'pages'} · tap to
              preview · drag to reorder
            </p>
          </div>
          {selectedIds.size > 0 && (
            <button
              type="button"
              onClick={handleDeleteSelected}
              className={`
                inline-flex items-center gap-2 rounded-full border
                border-red-300 bg-white px-4 py-2 text-sm font-medium
                text-red-600 transition-transform duration-100
                hover:bg-red-50
                active:scale-95
              `}
            >
              <FontAwesomeIcon icon={faTrashCan} className="text-xs" />
              <span className="hidden sm:inline">Delete </span>(
              {selectedIds.size})
            </button>
          )}
        </div>

        {error && (
          <p className="mb-4 text-sm text-red-600" role="alert">
            {error}
          </p>
        )}
        {isGenerating && uploadStatus && (
          <div className="mb-4 flex items-center gap-2 text-sm text-orange-500">
            <LoadingSpinner variant="small" />
            {uploadStatus}
          </div>
        )}

        {/* Thumbnail grid */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          // Re-measure droppables on every drag so add/delete never leaves
          // stale rects that make a drop land nowhere.
          measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={() => {
            skipPreviewClickRef.current = false;
          }}
        >
          <SortableContext
            items={pages.map((p) => p.id)}
            strategy={rectSortingStrategy}
          >
            <div
              className={`grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4`}
            >
              {pages.map((page, index) => (
                <SortablePageCard
                  key={page.id}
                  page={page}
                  index={index}
                  isSelected={selectedIds.has(page.id)}
                  disabled={isGenerating}
                  onToggleSelect={toggleSelected}
                  onPreview={openPreview}
                />
              ))}

              {/* Add-pages tile (not a sortable item) */}
              <button
                type="button"
                onClick={handleAddClick}
                onDragOver={handleAddDragOver}
                onDragLeave={handleAddDragLeave}
                onDrop={handleAddDrop}
                disabled={!canAddMore || isGenerating}
                aria-label="Add pages"
                className={`
                  flex aspect-[5/7] flex-col items-center justify-center gap-2
                  rounded-lg border border-dashed transition
                  ${
                    fileDrag === 'accept'
                      ? `
                        border-yellow-400 bg-yellow-50 text-yellow-500 ring-2
                        ring-yellow-400
                      `
                      : fileDrag === 'reject'
                        ? `
                          border-red-300 bg-red-50 text-red-400 ring-2
                          ring-red-300
                        `
                        : 'border-gray-300 bg-white text-gray-400'
                  }
                  hover:border-yellow-400 hover:text-yellow-500
                  disabled:cursor-not-allowed disabled:opacity-40
                  disabled:hover:border-gray-300 disabled:hover:text-gray-400
                `}
              >
                <FontAwesomeIcon icon={faPlus} className="text-2xl" />
                <span className="text-xs">
                  {!canAddMore
                    ? 'Page limit reached'
                    : fileDrag === 'accept'
                      ? 'Drop to add'
                      : fileDrag === 'reject'
                        ? 'JPG, PNG or WEBP only'
                        : 'Add or drop pages'}
                </span>
              </button>
            </div>
          </SortableContext>
        </DndContext>

        <input
          ref={addInputRef}
          type="file"
          accept={ADD_ACCEPT_ATTR}
          multiple
          className="hidden"
          aria-label="Add page images"
          onChange={(e) => {
            void handleAddFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </div>

      {previewPage && previewIndex >= 0 && (
        <PagePreviewLightbox
          page={previewPage}
          index={previewIndex}
          onClose={() => setPreviewId(null)}
        />
      )}
    </div>
  );
}

export const ScanReviewWithMsal = () => {
  return (
    <MsalAuthenticationTemplate interactionType={InteractionType.Redirect}>
      <ScanReview />
    </MsalAuthenticationTemplate>
  );
};
